/**
 * What `brand` and `storyworld` mean when the prompt is composed.
 *
 * The two used to compose byte-identical prompts: `mode` was carried all the way to the
 * resolver and then read by nobody. This module is the one place that turns it into behavior,
 * and it does so by returning a POLICY OBJECT rather than by exporting the mode. Everything
 * downstream consumes the policy, so `effectiveMemory.mode` has exactly one reader and cannot
 * grow a second interpretation somewhere else in the pipeline.
 *
 * THE FRAMING, ratified by James and deliberately not subtractive. A first design made brand
 * mode simply drop thesis and pillars, and he rejected it: its net effect was that brand mode
 * did LESS than storyworld, which is backwards for the mode that has the tighter requirements.
 * The accepted framing inverts what is LOAD-BEARING instead:
 *
 *   a storyworld thesis is the SETTING, and belongs in the picture
 *   a brand palette is a SPECIFICATION, and never stands aside
 *
 * So brand mode does not lose its positioning. Its positioning stops being read as image
 * direction, and its colours stop being optional.
 *
 * What that fixes, measured on the brand Bible in real data: `a debit card on a desk` composed
 * with non-visual positioning floating mid-prompt as bare sentences -- `no jargon, ever.` sat
 * there as a thing to draw -- while the colour spec was an ordinary optional clause that a
 * long prompt or a style reference could quietly remove. This is the same defect shape as
 * the labelled-palette-clause fix, one level up: a clause that does not say what it IS gets
 * read as a subject.
 */

/**
 * Storyworld's order is the historical one, and its policy encodes exactly today's behavior.
 * Any drift here shows up as a changed prompt digest in the world-consistency eval, which is
 * the acceptance proof that this change left storyworld alone.
 */
const STORYWORLD_SECTION_ORDER = ['direction', 'characters', 'palette', 'look', 'rules', 'feel'];

/**
 * Direction moves to the BACK in brand mode. Section order is only the tie-break -- weight
 * still dominates -- so a user who dials direction up to 0.9 gets it early even in brand.
 * The dial wins, deliberately: this policy sets a default, it does not overrule a person.
 */
const BRAND_SECTION_ORDER = ['characters', 'palette', 'look', 'rules', 'feel', 'direction'];

const STORYWORLD_POLICY = Object.freeze({
  mode: 'storyworld',
  sectionOrder: Object.freeze(STORYWORLD_SECTION_ORDER),
  colourSpecRequired: false,
  /**
   * MEASURED, then decided by James on 2026-08-18. This was `false`, on the reasoning that a
   * reference picture carries a look better than adjectives do.
   *
   * That reasoning holds for lighting and grading -- which still stand aside -- and does not
   * hold for colour. The reference-binding arm measured the colour clause moving the grade as
   * far as turning the ENTIRE Bible off:
   *
   *   noise floor (same prompt+condition, different seed)   n=30  mean 3.91
   *   whole Bible on vs off                                 n=10  mean 9.26   p=0.0038
   *   colour clause on vs off, behind a style reference     n=10  mean 9.22   p=0.0002
   *
   * ⚠️ The experiment could only be run in brand mode, and the reason is the thing this change
   * fixes: with `false`, storyworld's clause-present and clause-absent prompts are BYTE
   * IDENTICAL, because the clause is already suppressed. There was no manipulation to make.
   * So this is the brand measurement applied to storyworld on the strength of a shared
   * mechanism -- the same suppression code, the same clause -- and not a storyworld-specific
   * number. Stated rather than implied, because the two are not the same evidence.
   *
   * The carve-out remains scoped to `styleSuppressedByStyleReferenceAlone`: a model whose
   * profile already claims style from images still drops the colour spec, in both modes.
   */
  colourSpecSurvivesStyleRef: true,
  directionAsBrandAttitude: false,
  paletteFromPinnedBrandKit: false,
});

const BRAND_POLICY = Object.freeze({
  mode: 'brand',
  sectionOrder: Object.freeze(BRAND_SECTION_ORDER),
  /**
   * Required-rank, admitted after the rules and before any optional clause.
   *
   * "Never dropped" is budget-bounded and says so honestly. The composer will not trim the
   * user's own prompt (a pinned non-behavior), so a 280-character budget plus a long request
   * can defeat any clause. The guarantee is therefore: never dropped in favour of anything
   * else, and receipted under `budget` in the impossible case.
   */
  colourSpecRequired: true,
  /**
   * A style reference removes the whole look section today, colours included. For a
   * storyworld that is right -- a picture carries a look better than adjectives do. For a
   * brand it is wrong: the reference image is someone else's colours, and the brand's are a
   * specification, not a mood. Lighting and grading still stand aside, because a reference
   * genuinely does carry those better.
   */
  colourSpecSurvivesStyleRef: true,
  directionAsBrandAttitude: true,
  /**
   * The colour spec comes from the brand elements pinned in this Bible when any carries a kit.
   * The element owns the colours; `resolvePinnedBrandPalette` arbitrates; the Bible's phrases apply only when no pinned element has a kit.
   */
  paletteFromPinnedBrandKit: true,
});

export function getModeSteeringPolicy(mode) {
  return mode === 'brand' ? BRAND_POLICY : STORYWORLD_POLICY;
}

/**
 * Thesis and pillars as ONE labelled, atomic clause.
 *
 * Labelled, because that is the labelled-palette-clause lesson: a bare noun phrase is read as
 * a subject, and
 * `no jargon, ever.` sitting loose in an image prompt is an instruction to draw something.
 * `brand attitude:` names the clause's role so the model can weigh it as register rather
 * than as content.
 *
 * Atomic, because a partially dropped attitude is worse than an absent one -- the same reason
 * the colour palette drops as a unit. Half a positioning statement is a different positioning
 * statement.
 */
export function buildBrandAttitudeClause(snapshot) {
  const parts = [snapshot?.thesis, ...(snapshot?.pillars || [])]
    .filter((value) => typeof value === 'string' && value.trim())
    .map((value) => value.trim().replace(/\.$/, ''));
  const unique = [...new Set(parts)];
  return unique.length ? `brand attitude: ${unique.join('; ')}` : null;
}

// A receipt is a diagnostic, not a payload. Capped so a pathological Bible cannot bloat every
// generated image's persisted metadata.
const MAX_OMITTED_CLAUSES = 24;

/**
 * WHAT THE BIBLE DROPPED, AND WHY.
 *
 * These omissions already happened; what did not exist was any way to see them. Attaching a
 * style reference silently removes every palette and look clause -- measured at seven of
 * eleven facets, halving the prompt -- and the user is told nothing. Direction that
 * disappears without a trace is how a Bible stops being trusted, and an untrusted Bible is
 * worse than none.
 *
 * Section-weight omissions are deliberately NOT recorded: `suppressedSections` already
 * reports those, and the user switched them off themselves, so they are a setting rather
 * than a surprise.
 *
 * It lives here rather than in `sectionSteering.js` for room -- that file is against its size
 * budget -- and the fit is real: what counts as an omission is now mode-dependent. A brand's
 * colour spec that SURVIVED a style reference must never appear in the receipt, or the
 * receipt teaches the reader it cannot be trusted.
 */
export function collectOmittedClauses({
  styleSuppressed = false,
  suppressFramingClauses = false,
  sectionWeights,
  colourSpecSurvives = false,
  paletteClauses = [],
  paletteToneClauses = [],
  lookBaseClauses = [],
  framingClauses = [],
  dedupe = (values) => [...new Set(values)],
}) {
  const omitted = [];
  const record = (clauses, reason) => {
    for (const clause of dedupe(clauses)) {
      if (omitted.length >= MAX_OMITTED_CLAUSES) return;
      omitted.push({ clause, reason });
    }
  };
  if (styleSuppressed && sectionWeights.palette > 0) {
    record(colourSpecSurvives ? paletteToneClauses : paletteClauses, 'style-reference');
  }
  if (styleSuppressed && sectionWeights.look > 0) {
    record([...lookBaseClauses, ...framingClauses], 'style-reference');
  } else if (suppressFramingClauses && sectionWeights.look > 0) {
    // Held back for the shot, not for a reference image. Recorded separately so the reason is
    // honest: these two omissions look identical in the prompt and are not the same fact.
    record(framingClauses, 'framing-scope');
  }
  return omitted;
}
